import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Camera, CameraOff, Copy, Eye, Flashlight, FlashlightOff, KeyRound, Plus, RefreshCw, ScanLine, Search, Smartphone, Trash2, Upload, X } from "lucide-react";import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, ErrorState, LoadingState, Notice, PageHeader, StatusBadge } from "@/components/common/States";
import { SistLogo } from "@/components/common/SistLogo";
import KartuAnggota from "@/components/attendance/KartuAnggota";
import CetakKartuTab from "./CetakKartuTab";
import { cn } from "@/lib/utils";
import { useAuth, useSchoolCan } from "@/context/AuthContext";
import { api, errorMessage, fetcher } from "@/lib/api";
import {
  ATTENDANCE_STATUS, CARD_STATUS, CORRECTION_STATE, DEVICE_STATUS, EVENT_RESULT,
  ROLE_LABELS, formatDate,
} from "@/lib/labels";

const hariIni = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(new Date());

const TAUTAN_KARTU = /^https?:\/\/[^/]+\/kartu\/([\w.-]+)\/?$/i;

// Nama bundel mengandung hash build. Ditampilkan di layar pemindai supaya bisa memastikan
// HP memuat versi terbaru setelah deploy, tanpa perlu alat bantu pengembang.
const VERSI_BUILD = (() => {
  try {
    return (((document.querySelector('script[src*="main."]') || {}).src || "").match(/main\.([a-z0-9]{6,})\.js/) || [])[1] || "?";
  } catch {
    return "?";
  }
})();

const fmtJam = (iso) => (iso ? new Intl.DateTimeFormat("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit" }).format(new Date(iso)) : "—");

const Salin = ({ teks }) => (
  <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(teks); toast.success("Disalin."); }}>
    <Copy className="h-4 w-4 mr-1" /> Salin
  </Button>
);

// ---------- Monitor & Rekap ----------

const TabelKehadiran = ({ data, dapatKelola, onKoreksi }) => {
  if (!data) return <LoadingState />;
  if (!data.records.length && !data.tanpa_catatan?.length)
    return <EmptyState title="Belum ada catatan kehadiran" description="Belum ada tap RFID maupun koreksi pada tanggal ini." />;
  const rekap = data.records.reduce((a, r) => ({ ...a, [r.status]: (a[r.status] || 0) + 1 }), {});
  return (
    <div className="space-y-4" data-testid="attendance-records">
      <div className="flex flex-wrap gap-2" data-testid="attendance-summary">
        {Object.keys(ATTENDANCE_STATUS).map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold">
            {ATTENDANCE_STATUS[s].label}
            <span className="text-muted-foreground">{rekap[s] || 0}</span>
          </span>
        ))}
        {data.tanpa_catatan && (
          <span className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold">
            Tanpa catatan <span className="text-muted-foreground">{data.tanpa_catatan.length}</span>
          </span>
        )}
      </div>
      {!!data.records.length && (
        <div className="rounded-xl border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">No</TableHead>
                <TableHead>Nama</TableHead>
                <TableHead>Peran</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Tap pertama</TableHead>
                <TableHead>Tap terakhir</TableHead>
                <TableHead>Koreksi</TableHead>
                {dapatKelola && <TableHead className="w-24">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.records.map((r, i) => (
                <TableRow key={r.id} data-testid={`record-${r.id}`}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell className="font-medium">{r.member?.full_name || "—"}</TableCell>
                  <TableCell>{ROLE_LABELS[r.member?.role] || "—"}</TableCell>
                  <TableCell><StatusBadge map={ATTENDANCE_STATUS} value={r.status} /></TableCell>
                  <TableCell>{fmtJam(r.taps?.first)}</TableCell>
                  <TableCell>{fmtJam(r.taps?.last)}</TableCell>
                  <TableCell><StatusBadge map={CORRECTION_STATE} value={r.correction_state} /></TableCell>
                  {dapatKelola && (
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => onKoreksi(r)} data-testid={`correct-${r.id}`}>Koreksi</Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {!!data.tanpa_catatan?.length && (
        <div className="rounded-xl border p-4" data-testid="attendance-uncat">
          <p className="text-sm font-semibold mb-2">Siswa aktif tanpa catatan ({data.tanpa_catatan.length})</p>
          <p className="text-sm text-muted-foreground">{data.tanpa_catatan.map((m) => m.full_name).join(", ")}</p>
        </div>
      )}
    </div>
  );
};

const MonitorTab = ({ dapatKelola, onKoreksi }) => {
  const q = useQuery({ queryKey: ["attendance-records", hariIni()], queryFn: () => fetcher(`/school/attendance/records?d=${hariIni()}`) });
  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  return <TabelKehadiran data={q.data} dapatKelola={dapatKelola} onKoreksi={onKoreksi} />;
};

const RekapTab = ({ dapatKelola, onKoreksi }) => {
  const [d, setD] = useState(hariIni());
  const qClasses = useQuery({ queryKey: ["academic-classes"], queryFn: () => fetcher("/school/academic/classes") });
  const [classId, setClassId] = useState("semua");
  const q = useQuery({
    queryKey: ["attendance-records", d, classId],
    queryFn: () => fetcher(`/school/attendance/records?d=${d}${classId !== "semua" ? `&class_id=${classId}` : ""}`),
  });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1">
          <Label htmlFor="rekap-tanggal">Tanggal</Label>
          <Input id="rekap-tanggal" type="date" value={d} onChange={(e) => setD(e.target.value)} className="w-44" data-testid="rekap-date" />
        </div>
        <div className="space-y-1">
          <Label>Kelas</Label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="w-48" data-testid="rekap-class"><SelectValue placeholder="Semua kelas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="semua">Semua kelas</SelectItem>
              {(qClasses.data || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} />
        : <TabelKehadiran data={q.data} dapatKelola={dapatKelola} onKoreksi={onKoreksi} />}
    </div>
  );
};

// ---------- Kartu RFID ----------

const KartuTab = ({ dapatKelola }) => {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [cari, setCari] = useState("");
  const [mapping, setMapping] = useState(false);
  const [pratinjau, setPratinjau] = useState(null);
  const [uidBaru, setUidBaru] = useState("");
  const [memberId, setMemberId] = useState("");
  const q = useQuery({ queryKey: ["attendance-cards"], queryFn: () => fetcher("/school/attendance/cards") });
  const qMembers = useQuery({ queryKey: ["school-members"], queryFn: () => fetcher("/school/members") });
  const sudahPunya = useMemo(() => new Set((q.data || []).filter((c) => c.status === "active").map((c) => c.membership_id)), [q.data]);
  const calon = useMemo(() => (qMembers.data || []).filter((m) => m.role === "siswa" && m.status === "active" && !sudahPunya.has(m.id)), [qMembers.data, sudahPunya]);

  const kartu = useMemo(() => {
    const n = cari.trim().toLowerCase();
    return (q.data || []).filter((c) => !n || (c.seri || "").toLowerCase().includes(n) || (c.uid || "").toLowerCase().includes(n) || (c.member?.full_name || "").toLowerCase().includes(n));
  }, [q.data, cari]);

  const simpan = async () => {
    try {
      await api.post("/school/attendance/cards", { uid: uidBaru, membership_id: memberId });
      toast.success("Kartu berhasil dipetakan.");
      setMapping(false); setUidBaru(""); setMemberId("");
      qc.invalidateQueries();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const ubahStatus = async (c, status) => {
    const reason = status === "lost" ? window.prompt("Alasan kartu hilang:") : undefined;
    if (status === "lost" && !reason) return;
    try {
      await api.patch(`/school/attendance/cards/${c.id}`, { status, ...(reason ? { reason } : {}) });
      toast.success("Status kartu diperbarui.");
      qc.invalidateQueries();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="space-y-4" data-testid="attendance-cards">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden />
          <Input className="pl-8" placeholder="Cari nomor tercetak, UID, atau nama…" value={cari} onChange={(e) => setCari(e.target.value)} data-testid="card-search" />
        </div>
        {dapatKelola && (
          <Button onClick={() => setMapping(true)} data-testid="card-add"><Plus className="h-4 w-4 mr-1" /> Petakan kartu</Button>
        )}
      </div>
      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} />
        : !kartu.length ? <EmptyState title="Belum ada kartu RFID" description="Petakan kartu ke anggota sekolah untuk mulai mencatat presensi." />
        : (
          <div className="rounded-xl border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">No</TableHead>
                  <TableHead>Nomor tercetak</TableHead>
                  <TableHead>UID kartu</TableHead>
                  <TableHead>Pemegang</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24">Kartu</TableHead>
                  {dapatKelola && <TableHead className="w-56">Aksi</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {kartu.map((c, i) => (
                  <TableRow key={c.id} data-testid={`card-${c.id}`}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell className="font-mono text-sm select-all" data-testid={`card-seri-${c.id}`}>{c.seri || "—"}</TableCell>
                    <TableCell className="font-mono text-sm">{c.uid || "—"}</TableCell>
                    <TableCell>{c.member ? `${c.member.full_name} (${ROLE_LABELS[c.member.role]})` : "—"}</TableCell>
                    <TableCell><StatusBadge map={CARD_STATUS} value={c.status} /></TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => setPratinjau(c)} data-testid={`card-preview-${c.id}`}>
                        <Eye className="h-4 w-4 mr-1" /> Pratinjau
                      </Button>
                    </TableCell>
                    {dapatKelola && (
                      <TableCell className="space-x-1">
                        {c.status !== "active" && <Button variant="ghost" size="sm" onClick={() => ubahStatus(c, "active")}>Aktifkan</Button>}
                        {c.status === "active" && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => ubahStatus(c, "lost")}>Hilang</Button>
                            <Button variant="ghost" size="sm" onClick={() => ubahStatus(c, "inactive")}>Nonaktifkan</Button>
                          </>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      <Dialog open={!!pratinjau} onOpenChange={(b) => { if (!b) setPratinjau(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Kartu Anggota Sekolah</DialogTitle>
            <DialogDescription>Desain kartu yang muncul di layar pemindai saat anggota ini menempelkan kartunya.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-2">
            {pratinjau && (
              <KartuAnggota anggota={{ ...(pratinjau.member || {}), uid: pratinjau.uid }}
                sekolah={me?.active_school?.school_name} status="hadir" />
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={mapping} onOpenChange={setMapping}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Petakan kartu RFID</DialogTitle>
            <DialogDescription>Kartu hanya dapat dipetakan ke satu anggota aktif di sekolah ini.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="card-uid">UID kartu</Label>
              <Input id="card-uid" value={uidBaru} onChange={(e) => setUidBaru(e.target.value)} placeholder="mis. 04:A3:56:12:1F:0F" data-testid="card-uid-input" />
            </div>
            <div className="space-y-1">
              <Label>Pemegang kartu</Label>
              <Select value={memberId} onValueChange={setMemberId}>
                <SelectTrigger data-testid="card-member-select"><SelectValue placeholder="Pilih siswa" /></SelectTrigger>
                <SelectContent>
                  {calon.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMapping(false)}>Batal</Button>
            <Button onClick={simpan} disabled={!uidBaru.trim() || !memberId} data-testid="card-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Perangkat ----------

const PerangkatTab = ({ dapatKelola }) => {
  const qc = useQueryClient();
  const [buka, setBuka] = useState(false);
  const [form, setForm] = useState({ name: "", device_identity: "", location: "" });
  const [kunciBaru, setKunciBaru] = useState(null);
  const q = useQuery({ queryKey: ["attendance-devices"], queryFn: () => fetcher("/school/attendance/devices") });

  const simpan = async () => {
    try {
      const d = await api.post("/school/attendance/devices", form).then((r) => r.data);
      setKunciBaru({ nama: d.name, kunci: d.api_key });
      setBuka(false); setForm({ name: "", device_identity: "", location: "" });
      qc.invalidateQueries();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const ubahStatus = async (d, status) => {
    try {
      await api.patch(`/school/attendance/devices/${d.id}`, { status });
      toast.success("Status perangkat diperbarui.");
      qc.invalidateQueries();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const rotasi = async (d) => {
    try {
      const r = await api.post(`/school/attendance/devices/${d.id}/rotate-key`).then((x) => x.data);
      setKunciBaru({ nama: d.name, kunci: r.api_key });
      toast.success("Kunci perangkat diganti.");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="space-y-4" data-testid="attendance-devices">
      <div className="flex justify-end">
        {dapatKelola && <Button onClick={() => setBuka(true)} data-testid="device-add"><Plus className="h-4 w-4 mr-1" /> Tambah perangkat</Button>}
      </div>
      {kunciBaru && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-900 p-4 space-y-2" data-testid="device-key-shown" role="alert">
          <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
            Kunci API untuk “{kunciBaru.nama}” — hanya ditampilkan sekali. Simpan sekarang.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-white dark:bg-black/40 px-3 py-2 text-xs">{kunciBaru.kunci}</code>
            <Salin teks={kunciBaru.kunci} />
            <Button variant="ghost" size="sm" onClick={() => setKunciBaru(null)}>Tutup</Button>
          </div>
        </div>
      )}
      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} />
        : !q.data?.length ? <EmptyState title="Belum ada perangkat RFID" description="Daftarkan gerbang atau pemindai RFID untuk mulai menerima tap." />
        : (
          <div className="grid gap-3 md:grid-cols-2">
            {q.data.map((d) => (
              <div key={d.id} className="rounded-xl border p-4 space-y-2" data-testid={`device-${d.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold flex items-center gap-2"><ScanLine className="h-4 w-4" aria-hidden /> {d.name}</p>
                  <StatusBadge map={DEVICE_STATUS} value={d.status} />
                </div>
                <p className="text-sm text-muted-foreground">
                  {d.device_identity}{d.location ? ` · ${d.location}` : ""}
                </p>
                <p className="text-xs text-muted-foreground">
                  Terakhir terlihat: {d.last_seen_at ? formatDate(d.last_seen_at) : "belum pernah"}
                </p>
                {dapatKelola && (
                  <div className="flex gap-1 pt-1">
                    <Button variant="outline" size="sm" onClick={() => rotasi(d)} data-testid={`rotate-${d.id}`}>
                      <KeyRound className="h-4 w-4 mr-1" /> Ganti kunci
                    </Button>
                    {d.status === "active"
                      ? <Button variant="ghost" size="sm" onClick={() => ubahStatus(d, "inactive")}>Nonaktifkan</Button>
                      : <Button variant="ghost" size="sm" onClick={() => ubahStatus(d, "active")}>Aktifkan</Button>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      <Dialog open={buka} onOpenChange={setBuka}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Tambah perangkat RFID</DialogTitle>
            <DialogDescription>Identitas perangkat unik per sekolah, mis. RFID-SDBM001-01.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="dev-name">Nama perangkat</Label>
              <Input id="dev-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="mis. Gerbang Utama" data-testid="device-name-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="dev-id">Identitas perangkat</Label>
              <Input id="dev-id" value={form.device_identity} onChange={(e) => setForm({ ...form, device_identity: e.target.value })} placeholder="RFID-XXXX-NN" data-testid="device-identity-input" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="dev-loc">Lokasi (opsional)</Label>
              <Input id="dev-loc" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="mis. Pintu masuk utama" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBuka(false)}>Batal</Button>
            <Button onClick={simpan} disabled={form.name.trim().length < 2 || form.device_identity.trim().length < 3} data-testid="device-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------- Presensi manual oleh guru/wali kelas ----------

const EditorPresensi = ({ classId, d }) => {
  const qc = useQueryClient();
  const [override, setOverride] = useState({});
  const [busy, setBusy] = useState(false);
  const q = useQuery({
    queryKey: ["attendance-manual-roster", classId, d],
    queryFn: () => fetcher(`/school/attendance/manual/roster?class_id=${classId}&d=${d}`),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;

  const students = q.data.students;
  const statusOf = (s) => override[s.membership_id] ?? s.status ?? "HADIR";
  const semua = (status) => setOverride(Object.fromEntries(students.map((s) => [s.membership_id, status])));
  const jumlah = students.reduce((a, s) => ({ ...a, [statusOf(s)]: (a[statusOf(s)] || 0) + 1 }), {});

  const simpan = async () => {
    setBusy(true);
    try {
      const r = await api.post("/school/attendance/manual", {
        class_id: classId, date: d,
        entries: students.map((s) => ({ membership_id: s.membership_id, status: statusOf(s) })),
      }).then((x) => x.data);
      toast.success(`Presensi ${q.data.class.name} tersimpan. ${r.corrected ? `${r.corrected} catatan RFID dikoreksi.` : ""}`);
      setOverride({});
      qc.invalidateQueries();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="manual-editor">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap gap-2" data-testid="manual-summary">
          {Object.keys(ATTENDANCE_STATUS).map((st) => (
            <span key={st} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold">
              {ATTENDANCE_STATUS[st].label}<span className="text-muted-foreground">{jumlah[st] || 0}</span>
            </span>
          ))}
        </div>
        <div className="flex flex-wrap gap-1">
          <Button variant="outline" size="sm" onClick={() => semua("HADIR")} data-testid="manual-all-hadir">Semua hadir</Button>
          {Object.keys(ATTENDANCE_STATUS).filter((st) => st !== "HADIR").map((st) => (
            <Button key={st} variant="ghost" size="sm" onClick={() => semua(st)} data-testid={`manual-all-${st.toLowerCase()}`}>Semua {ATTENDANCE_STATUS[st].label.toLowerCase()}</Button>
          ))}
        </div>
      </div>
      <div className="rounded-xl border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">No</TableHead>
              <TableHead>Nama</TableHead>
              <TableHead>Catatan saat ini</TableHead>
              <TableHead className="w-44">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {students.map((s, i) => {
              const cur = statusOf(s);
              const tercatat = override[s.membership_id] === undefined && s.status !== null && s.status !== undefined;
              return (
                <TableRow key={s.membership_id} data-testid={`manual-student-${s.membership_id}`}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell className="font-medium">{s.full_name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {tercatat ? `${ATTENDANCE_STATUS[s.status]?.label || s.status}${s.record_source === "rfid" ? " (RFID)" : " (manual)"}` : "—"}
                  </TableCell>
                  <TableCell>
                    <Select value={cur} onValueChange={(v) => setOverride({ ...override, [s.membership_id]: v })}>
                      <SelectTrigger data-testid={`manual-status-${s.membership_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.keys(ATTENDANCE_STATUS).map((st) => <SelectItem key={st} value={st}>{ATTENDANCE_STATUS[st].label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-end">
        <Button onClick={simpan} disabled={busy || !students.length} data-testid="manual-save">
          {busy ? "Menyimpan…" : `Simpan presensi (${students.length} siswa)`}
        </Button>
      </div>
    </div>
  );
};

const PresensiManualTab = () => {
  const [d, setD] = useState(hariIni());
  const qClasses = useQuery({ queryKey: ["attendance-manual-classes"], queryFn: () => fetcher("/school/attendance/manual/classes") });
  const [classId, setClassId] = useState("");

  if (qClasses.isLoading) return <LoadingState />;
  if (qClasses.error) return <ErrorState error={qClasses.error} onRetry={qClasses.refetch} />;
  if (!qClasses.data.length)
    return <EmptyState title="Tidak ada kelas yang dapat diabsen" description="Anda hanya dapat mengabsen kelas yang Anda ampuh atau kelas yang Anda wali." />;

  const dipilih = classId && qClasses.data.some((k) => k.id === classId) ? classId : qClasses.data[0].id;
  const info = qClasses.data.find((k) => k.id === dipilih);

  return (
    <div className="space-y-4" data-testid="attendance-manual">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1">
          <Label htmlFor="manual-tanggal">Tanggal</Label>
          <Input id="manual-tanggal" type="date" value={d} max={hariIni()} onChange={(e) => setD(e.target.value)} className="w-44" data-testid="manual-date" />
        </div>
        <div className="space-y-1">
          <Label>Kelas</Label>
          <Select value={dipilih} onValueChange={setClassId}>
            <SelectTrigger className="w-56" data-testid="manual-class"><SelectValue /></SelectTrigger>
            <SelectContent>
              {qClasses.data.map((k) => (
                <SelectItem key={k.id} value={k.id}>{k.name}{k.is_homeroom ? " (wali kelas)" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground pb-2">{info?.student_count ?? 0} siswa · perubahan terhadap catatan RFID tercatat sebagai koreksi beralasan.</p>
      </div>
      <EditorPresensi key={`${dipilih}-${d}`} classId={dipilih} d={d} />
    </div>
  );
};

// ---------- Pemindai HP (NFC + QR) ----------

const kunciAntrean = (sid) => `sist-scan-queue:${sid || "umum"}`;
const STATUS_TAP = {
  menunggu: { label: "Menunggu", tone: "info" },
  diterima: { label: "Hadir tercatat", tone: "ok" },
  sudah_ada: { label: "Sudah tercatat hari ini", tone: "warn" },
  ditolak: { label: "Ditolak", tone: "danger" },
  duplikat: { label: "Event ganda", tone: "warn" },
};

// Tap kedua di hari yang sama tetap tercatat sebagai event, tapi tidak menimpa catatan yang sudah ada.
const kunciHasilTap = (r) => (r.status === "diterima" && !r.record_baru ? "sudah_ada" : r.status);
const alasanHasilTap = (r) => r.reason || (r.status === "diterima" && !r.record_baru
  ? `Catatan hari ini: ${ATTENDANCE_STATUS[r.record_status]?.label || r.record_status || "—"}`
  : null);

// Record teks NDEF = [status byte][kode bahasa][isi], dan Chrome di HP owner terbukti tidak
// mengembalikan apa pun dari toText(). Jadi payload-nya diurai sendiri: tanpa ini kartu yang sudah
// ditulis ulang tetap jatuh ke nomor chipnya, yaitu pemegang lama.
const layakPakai = (s) => {
  const t = (s || "").trim();
  return t && !/[\u0000-\u001f\u007f\ufffd]/.test(t) ? t : null;
};

const bytesDari = (d) => {
  if (!d) return null;
  if (d instanceof ArrayBuffer) return new Uint8Array(d);
  if (ArrayBuffer.isView(d)) return new Uint8Array(d.buffer, d.byteOffset, d.byteLength);
  if (typeof d === "string") return new TextEncoder().encode(d);
  try { return Uint8Array.from(d, (x) => Number(x) & 0xff); } catch { return null; }
};

// Bentuk record NFC Tools tidak selalu sama di setiap build Chrome (tipe bisa text/plain, MIME,
// atau kosong), jadi beberapa susunan payload dicoba berurutan dan yang paling layak dipakai menang.
const teksRecord = (rec) => {
  try {
    const hasil = rec.toText?.();
    const nilai = typeof hasil === "string" ? hasil : hasil?.value;
    if (layakPakai(nilai)) return layakPakai(nilai);
  } catch { /* record bukan text/plain */ }
  try {
    const url = rec.toURL?.();
    const nilai = typeof url === "string" ? url : url?.href;
    if (nilai) return nilai.trim();
  } catch { /* record bukan URI */ }

  const byte = bytesDari(rec?.data);
  if (!byte?.length) return null;
  // Panjang kode bahasa dihitung dalam byte, jadi offset-nya sama untuk UTF-8 maupun UTF-16.
  const isi = byte.slice(1 + (byte[0] & 0x3f));
  const coba = (arr, enc) => {
    try { return layakPakai(new TextDecoder(enc).decode(arr)); } catch { return null; }
  };
  const kandidat = [
    coba(byte, "utf-8"),
    coba(isi, "utf-8"),
    coba(isi, "utf-16be"),
    coba(isi, "utf-16le"),
  ].filter(Boolean);
  // Salah urutan byte UTF-16 menghasilkan karakter asing yang lolos cek kontrol, jadi kandidat
  // ASCII (nomor kartu selalu ASCII) yang dipilih lebih dulu.
  return kandidat.find((t) => /^[\x20-\x7e]+$/.test(t)) || kandidat[0] || null;
};

// Nomor chip (serialNumber) sengaja tidak dipakai sebagai identitas: ia tidak ikut berubah saat
// kartu didaur ulang, sehingga tap bisa menimpa pemegang lama.
const jejakRecord = (rec) => {
  const byte = bytesDari(rec?.data);
  const hexa = byte ? [...byte.slice(0, 20)].map((b) => b.toString(16).padStart(2, "0")).join(" ") : "—";
  return `${rec?.recordType || "tanpa tipe"} · ${byte?.length ?? 0} byte · ${hexa}`;
};

const isiKartu = (e) => {
  const records = e.message?.records || [];
  for (const rec of records) {
    const teks = teksRecord(rec);
    if (teks) return { teks, dari: "teks kartu" };
  }
  // Isi mentah ikut ditulis: kalau masih ada bentuk record yang belum terurai, jejaknya langsung
  // menunjukkan penyebabnya tanpa perlu menebak dari HP.
  return { teks: null, dari: records.length ? jejakRecord(records[0]) : "0 record" };
};

// Alasan kegagalan dibaca dari DOMException, bukan ditebak — supaya pengguna tahu persis apa yang harus diubah.
const GALAT_KAMERA = {
  NotAllowedError: "Izin kamera belum diberikan ke situs ini. Ketuk ikon kamera/gembok di bilah alamat Chrome → Izinkan, lalu tekan Mulai SCAN lagi.",
  NotFoundError: "Perangkat ini tidak punya kamera yang bisa dipakai browser.",
  NotReadableError: "Kamera sedang dipakai aplikasi lain (telepon video, aplikasi Kamera). Tutup aplikasi itu lalu coba lagi.",
  OverconstrainedError: "Kamera belakang tidak bisa dipilih di browser ini.",
  SecurityError: "Halaman harus dibuka lewat HTTPS supaya kamera boleh aktif.",
};

const GALAT_NFC = {
  NotSupportedError: "NFC HP belum menyala, atau browser ini tidak mendukung Web NFC. Nyalakan NFC di pengaturan HP lalu coba lagi.",
  NotAllowedError: "Permintaan NFC barusan ditolak browser. Ketuk tombol “Nyalakan NFC” yang putih di bawah layar — satu ketukan jari sudah cukup. Bila tetap ditolak, izinkan akses “Perangkat di sekitar saya” untuk situs ini di pengaturan Chrome.",
  NetworkError: "Radio NFC sedang dipakai aplikasi lain (dompet/tap bayar). Tutup aplikasi itu lalu coba lagi.",
};

// Getar dipakai sebagai umpan balik "terbaca / gagal", dipanggil satu kali per kejadian — bukan tiap bingkai kamera.
const getar = (terima) => navigator.vibrate?.(terima ? [50, 30, 50] : [180, 80, 180]);

// Layar penuh ala mesin absen khusus QR: kamera hidup terus, kartu muncul 3 detik tiap scan berhasil.
// NFC sengaja tidak muncul di layar ini — perangkat gerbang pemindai kartu memakai tombol "Pindai NFC".
const ModeKiosk = ({ sekolah, perangkat, kameraAktif, senterBisa, senter, videoRef, versi, galatKamera,
  onSenter, onKamera, onKeluar, antrean, tercatat, kartu }) => (
  <div className="fixed inset-0 z-[60] h-[100dvh] bg-black" data-testid="scan-kiosk" role="dialog" aria-modal="true" aria-label="Mode scan presensi">
    <video ref={videoRef} className={cn("absolute inset-0 h-full w-full object-cover", !kameraAktif && "opacity-0")} playsInline muted data-testid="kiosk-video" />
    <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/20 to-black/85" aria-hidden />

    <header className="relative flex items-start justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <SistLogo size="md" inverted tagline={false} />
        <p className="mt-1 truncate text-[11px] text-white/70">{sekolah}{perangkat ? ` · ${perangkat}` : ""}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {senterBisa && (
          <button type="button" onClick={onSenter} aria-pressed={senter} data-testid="kiosk-torch"
            className={cn("grid h-10 w-10 place-items-center rounded-full border text-white",
              senter ? "border-sky-300 bg-sky-500/30" : "border-white/25 bg-white/10")}>
            {senter ? <Flashlight className="h-5 w-5" /> : <FlashlightOff className="h-5 w-5" />}
          </button>
        )}
        <button type="button" onClick={onKeluar} data-testid="kiosk-exit"
          className="inline-flex h-10 items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-4 text-sm font-semibold text-white">
          <X className="h-4 w-4" /> Keluar
        </button>
      </div>
    </header>

    <div className="relative flex h-[calc(100%-12rem)] flex-col items-center justify-center gap-7 px-6">
      <div className="relative h-56 w-56 sm:h-72 sm:w-72" aria-hidden>
        {[["left-0 top-0 rounded-tl-2xl border-l-4 border-t-4"], ["right-0 top-0 rounded-tr-2xl border-r-4 border-t-4"],
          ["bottom-0 left-0 rounded-bl-2xl border-b-4 border-l-4"], ["bottom-0 right-0 rounded-br-2xl border-b-4 border-r-4"]].map(([kls]) => (
          <span key={kls} className={cn("absolute h-12 w-12 border-white/90", kls)} />
        ))}
        <span className="sist-scan absolute inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-sky-300 to-transparent" />
      </div>
      <div className="text-center">
        <p className="text-lg font-bold leading-snug text-white sm:text-2xl">Silakan scan kode QR Anda</p>
        {senterBisa && <p className="mt-2 text-xs text-white/60">Sentuh ikon lampu untuk menerangi</p>}
        {!kameraAktif && <p className="mt-2 text-xs text-white/60">Kamera belum hidup — ketuk “Coba kamera” di bawah.</p>}
      </div>
    </div>

    <footer className="absolute inset-x-0 bottom-0 space-y-2 px-4 pb-6">
      {galatKamera && <p className="rounded-lg bg-rose-500/95 px-3 py-2 text-xs font-semibold text-white" data-testid="kiosk-camera-error">{galatKamera}</p>}
      {!!antrean && <p className="rounded-lg bg-sky-500/90 px-3 py-2 text-xs font-semibold text-white" data-testid="kiosk-queue">{antrean} tap menunggu terkirim…</p>}
      {!kameraAktif && (
        <div className="flex justify-center">
          <button type="button" onClick={onKamera} data-testid="kiosk-camera"
            className="rounded-full border border-white/25 bg-white/10 px-5 py-2 text-sm font-semibold text-white">
            Coba kamera
          </button>
        </div>
      )}
      <p className="text-center text-sm font-semibold text-white sm:text-base" data-testid="kiosk-count">
        {tercatat} anggota tercatat pada sesi ini
      </p>
      <p className="absolute bottom-1.5 right-4 font-mono text-[10px] text-white/45" data-testid="kiosk-versi">versi {versi}</p>
    </footer>

    {/* Lebar kartu dikunci ke 100vw supaya tidak pernah terpotong di layar HP berapa pun ukurannya,
        dan lapisan ini fixed (bukan absolute) supaya pusatnya selalu pusat layar, bukan pusat induk. */}
    {kartu && (
      <div className="fixed inset-0 z-[70] flex overflow-y-auto overscroll-contain bg-black/70 px-3 py-4 backdrop-blur-sm" data-testid="kiosk-kartu">
        <div className="sist-rise m-auto w-[calc(100vw-1.5rem)] max-w-[23rem]">
          <KartuAnggota anggota={kartu.anggota || { uid: kartu.uid }} sekolah={sekolah} status={kartu.status}
            waktu={kartu.waktu} perangkat={perangkat} alasan={kartu.alasan} />
        </div>
      </div>
    )}
  </div>
);

const STATUS_KARTU = { diterima: "hadir", sudah_ada: "sudah", duplikat: "sudah", ditolak: "ditolak" };

const ScannerTab = () => {
  const { me } = useAuth();
  const qc = useQueryClient();
  const antreanKey = useMemo(() => kunciAntrean(me?.active_school?.school_id), [me?.active_school?.school_id]);
  const qDev = useQuery({ queryKey: ["scan-devices"], queryFn: () => fetcher("/school/attendance/scan/devices") });
  const [devId, setDevId] = useState(() => localStorage.getItem("sist-scan-device") || "");
  const [antrean, setAntrean] = useState(() => {
    try { return JSON.parse(localStorage.getItem(kunciAntrean(me?.active_school?.school_id))) || []; } catch { return []; }
  });
  const [tapTerakhir, setTapTerakhir] = useState(null);
  const [riwayat, setRiwayat] = useState([]);
  const [pesan, setPesan] = useState(null);
  const [sibuk, setSibuk] = useState(false);
  const [nfcAktif, setNfcAktif] = useState(false);
  const [kameraAktif, setKameraAktif] = useState(false);
  const [uidManual, setUidManual] = useState("");
  const [kiosk, setKiosk] = useState(false);
  const [kartuPopup, setKartuPopup] = useState(null);
  const [tercatat, setTercatat] = useState(0);
  const [senter, setSenter] = useState(false);
  const [senterBisa, setSenterBisa] = useState(false);
  const [infoKamera, setInfoKamera] = useState("");
  const [galatKamera, setGalatKamera] = useState(null);
  const [jejak, setJejak] = useState([]);
  const antreanRef = useRef(antrean);
  const sedangKirim = useRef(false);
  const pembacaNFC = useRef(null);
  const videoEl = useRef(null);
  const stream = useRef(null);
  const pemindaiLoop = useRef(0);
  const kartuTerakhir = useRef({ uid: "", at: 0 });
  const timerKartu = useRef(0);
  const timerCek = useRef(0);
  const sudahBaca = useRef(false);

  useEffect(() => { antreanRef.current = antrean; localStorage.setItem(antreanKey, JSON.stringify(antrean)); }, [antrean, antreanKey]);
  useEffect(() => { if (devId) localStorage.setItem("sist-scan-device", devId); }, [devId]);

  const hentikanKamera = useCallback(() => {
    clearTimeout(pemindaiLoop.current);
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    if (videoEl.current) videoEl.current.srcObject = null;
    setKameraAktif(false);
    setSenter(false);
    setSenterBisa(false);
    setInfoKamera("");
  }, []);

  useEffect(() => () => {
    hentikanKamera();
    try { pembacaNFC.current?.abort?.(); } catch { /* sudah berhenti */ }
    clearTimeout(timerKartu.current);
    clearTimeout(timerCek.current);
  }, [hentikanKamera]);

  // Elemen video berpindah antara panel tab dan layar penuh, jadi aliran kamera dipasang lewat callback ref.
  const pasangVideo = useCallback((node) => {
    videoEl.current = node;
    if (node && stream.current) { node.srcObject = stream.current; node.play().catch(() => {}); }
  }, []);

  // Layar penuh bisa terpasang lebih dulu sebelum aliran kamera ada — kejar dari sisi state juga.
  useEffect(() => {
    if (!videoEl.current) return;
    videoEl.current.srcObject = stream.current || null;
    if (stream.current) videoEl.current.play().catch(() => {});
  }, [kameraAktif]);

  const perangkat = qDev.data || [];
  const devDipilih = perangkat.some((d) => d.id === devId) ? devId : perangkat[0]?.id || "";
  const namaPerangkat = perangkat.find((d) => d.id === devDipilih)?.name || "";
  // Dibaca lewat ref supaya pemindai NFC/QR yang sedang aktif selalu memakai perangkat terbaru.
  const devAktif = useRef("");
  const namaDev = useRef("");
  useEffect(() => {
    devAktif.current = devDipilih;
    namaDev.current = (qDev.data || []).find((d) => d.id === devDipilih)?.name || "";
  }, [devDipilih, qDev.data]);

  // Satu baris riwayat per tap: status "menunggu" ditimpa oleh balasan server, bukan ditambah baris baru.
  const tampilkan = useCallback((baris) => {
    setTapTerakhir(baris);
    setRiwayat((h) => {
      const i = baris.id ? h.findIndex((x) => x.id === baris.id) : -1;
      if (i < 0) return [baris, ...h].slice(0, 8);
      const salin = [...h];
      salin[i] = baris;
      return salin;
    });
  }, []);

  // Kartu anggota muncul 3 detik, lalu layar kembali siap memindai — persis layar mesin absen di gerbang.
  const munculkanKartu = useCallback((isi) => {
    clearTimeout(timerKartu.current);
    setKartuPopup(isi);
    timerKartu.current = setTimeout(() => setKartuPopup(null), 3000);
  }, []);

  const kirim = useCallback(async () => {
    const events = antreanRef.current.slice(0, 50);
    if (!events.length || sedangKirim.current) return;
    if (!devAktif.current) { setPesan("Pilih perangkat pemindai dulu sebelum mencatat tap."); return; }
    sedangKirim.current = true;
    setSibuk(true);
    const dikirim = new Set(events.map((e) => e.event_id));
    try {
      const { data } = await api.post("/school/attendance/scan", { device_id: devAktif.current, events });
      for (const r of data.results) {
        const status = kunciHasilTap(r);
        const hasilKartu = STATUS_KARTU[status] || "ditolak";
        tampilkan({ id: r.event_id, uid: r.uid, status, nama: r.member?.full_name,
          alasan: alasanHasilTap(r), anomali: r.time_anomaly, kartu: r.member || null });
        munculkanKartu({ status: hasilKartu, anggota: r.member || null, uid: r.uid,
          waktu: data.received_at || new Date().toISOString(), perangkat: namaDev.current,
          alasan: r.member ? alasanHasilTap(r) : (alasanHasilTap(r) || "Kartu tidak dikenal.") });
        if (hasilKartu === "hadir") setTercatat((n) => n + 1);
      }
      setAntrean((a) => a.filter((x) => !dikirim.has(x.event_id)));
      setPesan(null);
      qc.invalidateQueries();
    } catch (e) {
      if (e.response) {
        const pesanSalah = errorMessage(e);
        for (const x of events) tampilkan({ id: x.event_id, uid: x.uid, status: "ditolak", alasan: pesanSalah });
        setAntrean((a) => a.filter((x) => !dikirim.has(x.event_id)));
        setPesan(pesanSalah);
      } else {
        setPesan("Tidak ada koneksi internet. Tap tersimpan di HP ini dan terkirim otomatis saat koneksi pulih.");
      }
    } finally {
      sedangKirim.current = false;
      setSibuk(false);
    }
  }, [qc, tampilkan, munculkanKartu]);

  useEffect(() => {
    if (!antrean.length) return;
    const saatDaring = () => kirim();
    window.addEventListener("online", saatDaring);
    const berkala = setInterval(saatDaring, 20000);
    return () => { window.removeEventListener("online", saatDaring); clearInterval(berkala); };
  }, [antrean.length, kirim]);

  // Jejak NFC: apa yang dilaporkan radio HP, apa adanya. Tanpa ini "kartu tidak terbaca" bisa berarti
  // lima hal berbeda yang tidak bisa dibedakan dari jauh.
  const catatJejak = useCallback((teks) => setJejak((j) => [
    `${new Date().toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", second: "2-digit" })} ${teks}`,
    ...j,
  ].slice(0, 4)), []);

  const catat = useCallback((teks, sumber) => {
    sudahBaca.current = true;
    const bersih = String(teks || "").trim();
    // Kartu ID cetak memuat tautan /kartu/<kunci>; kuncinya huruf kecil sehingga tidak boleh di-besar-kan.
    const cocok = bersih.match(TAUTAN_KARTU);
    const uid = cocok ? cocok[1]
      : /^https?:\/\//i.test(bersih) ? (bersih.split("?")[0].split("/").filter(Boolean).pop() || "")
        : bersih.toUpperCase();
    if (uid.length < 3) {
      getar(false);
      catatJejak(`${sumber}: "${teks || "kosong"}" bukan nomor kartu`);
      setPesan(`${sumber}: "${teks || "kosong"}" bukan nomor kartu yang dikenali. Kartu RFID harus diformat NDEF berisi UID-nya.`);
      return false;
    }
    const sekarang = Date.now();
    const jeda = Math.round((sekarang - kartuTerakhir.current.at) / 1000);
    if (kartuTerakhir.current.uid === uid && jeda < 8) {
      // Kartu yang sama sengaja diabaikan sebentar supaya satu tempelan tidak menjadi dua catatan.
      // Tanpa notifikasi, HP seolah buta kartu — padahal radionya justru baru saja berhasil.
      getar(true);
      catatJejak(`${sumber}: kartu sama terdeteksi lagi (${jeda} dtk lalu) → diabaikan`);
      setPesan(`Kartu ${uid} barusan terdeteksi ${jeda} detik lalu dan sudah diproses. Tunggu beberapa detik atau pindai kartu lain.`);
      return false;
    }
    getar(true);
    catatJejak(`${sumber}: nomor ${uid} dikirim ke server`);
    kartuTerakhir.current = { uid, at: sekarang };
    const event = {
      event_id: crypto.randomUUID ? crypto.randomUUID() : `tap-${sekarang}-${Math.random().toString(36).slice(2, 10)}`,
      uid, at: new Date().toISOString(),
    };
    antreanRef.current = [...antreanRef.current, event];
    setAntrean(antreanRef.current);
    tampilkan({ id: event.event_id, uid, status: "menunggu", sumber });
    kirim();
    return true;
  }, [kirim, tampilkan, catatJejak]);

  const nfcDidukung = typeof window !== "undefined" && "NDEFReader" in window;
  const qrDidukung = typeof window !== "undefined" && "BarcodeDetector" in window;
  const infoAlat = [
    nfcDidukung ? "browser mendukung NFC" : "browser ini tidak mendukung NFC (harus Chrome di Android)",
    qrDidukung ? "QR otomatis aktif" : "QR otomatis tidak didukung browser ini",
    kameraAktif ? `kamera hidup ${infoKamera}` : "kamera mati",
    nfcAktif ? "NFC siap membaca kartu" : "NFC tidak sedang membaca",
    `versi aplikasi ${VERSI_BUILD}`,
  ].join(" · ");

  const matikanNFC = useCallback(async () => {
    try { await pembacaNFC.current?.abort?.(); } catch { /* sudah berhenti */ }
    pembacaNFC.current = null;
    setNfcAktif(false);
    catatJejak("NFC dimatikan");
  }, [catatJejak]);

  const nyalakanNFC = useCallback(async () => {
    if (nfcAktif) return true;
    if (!nfcDidukung) { catatJejak("browser ini tidak punya Web NFC (harus Chrome Android)"); setPesan("Browser ini tidak mendukung NFC. Buka situs lewat Chrome di Android, atau gunakan kamera QR / isi nomor kartu manual."); return false; }
    try {
      catatJejak("meminta izin NFC ke browser...");
      const pembaca = new NDEFReader();
      pembaca.onreading = (e) => {
        const { teks, dari } = isiKartu(e);
        const jumlah = e.message?.records?.length || 0;
        catatJejak(`kartu terdeteksi · ${jumlah} record · ${teks ? `teks kartu: "${String(teks).slice(0, 24)}"` : `TIDAK TERURAI: ${dari}`} · chip: ${e.serialNumber || "—"}`);
        if (!teks) {
          sudahBaca.current = true;
          getar(false);
          setPesan(jumlah
            ? `HP membaca kartunya dan ada ${jumlah} record, tapi isinya belum dikenali sebagai nomor kartu, jadi tap tidak diteruskan. Bentuk rekaman: ${dari}. Kalau kartu memang masih kosong: tulis nomor tercetaknya (mis. SDBM-2026-0003) lewat NFC Tools → Write → Add record → Text, jangan pilih “Format as NDEF”. Kalau isinya sudah SDBM-2026-0003 dan masih gagal, foto kotak abu-abu di atas — bentuk rekaman di atas yang saya butuhkan untuk mencocokkannya.`
            : `Kartu${e.serialNumber ? ` ${e.serialNumber}` : ""} terbaca HP tapi tidak berisi teks nomor, jadi tap tidak diteruskan. Nomor chip tidak dipakai karena tidak ikut berubah saat kartu didaur ulang. Tulis nomor tercetak kartunya (mis. SDBM-2026-0003) sebagai satu record Text lewat NFC Tools → Write → Add record → Text, lalu pindai lagi.`);
          return;
        }
        catat(teks, "NFC");
      };
      pembaca.onreadingerror = () => {
        catatJejak("kartu menempel tapi isinya ditolak browser");
        getar(false);
        sudahBaca.current = true;
        setPesan("HP mendeteksi kartunya, tapi isinya tidak bisa dibaca browser. Kartu Mifare Classic harus diformat ke NDEF agar terbaca dari browser.");
        toast.error("Kartu gagal dibaca.");
      };
      pembaca.ondisconnected = () => { catatJejak("sesi NFC diputus browser/HP"); setNfcAktif(false); setPesan("Radio NFC berhenti sendiri — pastikan NFC di HP tetap menyala dan aplikasi ini tetap di depan."); };
      pembacaNFC.current = pembaca;
      await pembaca.scan();
      setNfcAktif(true);
      catatJejak("NFC aktif — menunggu kartu ditempel");
      setPesan(null);
      return true;
    } catch (e) {
      catatJejak(`browser menolak NFC: ${e?.name || "?"} — ${e?.message || "tanpa pesan"}`);
      setPesan(GALAT_NFC[e?.name] || "NFC belum dapat aktif: " + (e?.message || "pastikan NFC HP sudah menyala."));
      return false;
    }
  }, [nfcAktif, nfcDidukung, catat, catatJejak]);

  const bukaKamera = async () => {
    if (kameraAktif) return true;
    setGalatKamera(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setGalatKamera("Browser ini tidak memberi akses kamera. Buka lewat Chrome Android atau Safari iOS dengan alamat https://.");
      return false;
    }
    // Coba kamera belakang keras → lembut → apa saja, karena tidak semua HP melaporkan facingMode.
    let video = null;
    let galat = null;
    const resolusi = { width: { ideal: 1280 }, height: { ideal: 720 } };
    for (const opsi of [{ video: { ...resolusi, facingMode: { exact: "environment" } } }, { video: { ...resolusi, facingMode: "environment" } }, { video: true }]) {
      try { video = await navigator.mediaDevices.getUserMedia(opsi); break; }
      catch (e) { galat = e; if (e?.name !== "OverconstrainedError") break; }
    }
    if (!video) {
      setGalatKamera(GALAT_KAMERA[galat?.name] || "Kamera tidak dapat dibuka: " + (galat?.message || galat?.name || "galat tidak diketahui."));
      return false;
    }
    stream.current = video;
    setKameraAktif(true);
    const setelan = video.getVideoTracks()[0]?.getSettings?.() || {};
    const sisi = setelan.facingMode === "environment" ? "belakang" : setelan.facingMode === "user" ? "depan" : setelan.facingMode || "?";
    setInfoKamera(`${setelan.width || "?"}x${setelan.height || "?"} ${sisi}`);
    setPesan(qrDidukung ? null : "Kamera hidup, tapi browser ini tidak bisa membaca QR otomatis — pakai NFC atau ketik nomor kartu.");
    try { setSenterBisa(!!video.getVideoTracks()[0]?.getCapabilities?.().torch); } catch { setSenterBisa(false); }
    if (qrDidukung) {
      const detektor = new window.BarcodeDetector({ formats: ["qr_code"] });
      const telusuri = async () => {
        if (!stream.current) return;
        try {
          const kode = videoEl.current ? await detektor.detect(videoEl.current) : [];
          if (kode.length) catat(kode[0].rawValue, "QR");
        } catch { /* bingkai kamera belum siap */ }
        pemindaiLoop.current = setTimeout(telusuri, 400);
      };
      telusuri();
    }
    return true;
  };

  const gantiSenter = async () => {
    const trek = stream.current?.getVideoTracks()[0];
    if (!trek) return;
    try {
      await trek.applyConstraints({ advanced: [{ torch: !senter }] });
      setSenter(!senter);
    } catch { setPesan("Lampu senter tidak dapat dinyalakan dari browser ini."); }
  };

  const keluarKiosk = useCallback(() => {
    setKiosk(false);
    setKartuPopup(null);
    hentikanKamera();
  }, [hentikanKamera]);

  // Mulai SCAN khusus QR. Kartu NFC memakai tombol "Pindai NFC" (perangkat/akun petugas berbeda),
  // jadi dua layar gerbang tidak pernah merebut radio yang sama.
  const masukKiosk = async () => {
    sudahBaca.current = false;
    setKiosk(true);
    setTercatat(0);
    setPesan(null);
    await bukaKamera();
  };

  // 15 detik NFC aktif tanpa satu bacaan pun: tampilkan daftar periksa, jangan diam.
  useEffect(() => {
    clearTimeout(timerCek.current);
    if (!nfcAktif) return undefined;
    timerCek.current = setTimeout(() => {
      if (sudahBaca.current) return;
      catatJejak("15 detik NFC aktif: radio tidak melaporkan kartu apa pun");
      setPesan("Belum ada kartu yang terbaca. Cek tiga hal: (1) NFC di HP sudah menyala, (2) layar menyala dan halaman ini paling depan, (3) kartu ditempel di antena NFC — biasanya di punggung atas dekat kamera, lepas casing tebal. Kartu lama yang belum diformat NDEF tidak bisa dibaca browser.");
    }, 15000);
    return () => clearTimeout(timerCek.current);
  }, [nfcAktif, catatJejak]);

  useEffect(() => {
    if (!kiosk) return undefined;
    const tombol = (e) => { if (e.key === "Escape") keluarKiosk(); };
    document.addEventListener("keydown", tombol);
    const scrollSebelum = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", tombol);
      document.body.style.overflow = scrollSebelum;
    };
  }, [kiosk, keluarKiosk]);

  if (qDev.isLoading) return <LoadingState />;
  if (qDev.error) return <ErrorState error={qDev.error} onRetry={qDev.refetch} />;

  return (
    <div className="space-y-4" data-testid="attendance-scanner">
      {!perangkat.length ? (
        <EmptyState title="Belum ada perangkat aktif" description="Daftarkan perangkat RFID di tab Perangkat lebih dulu, lalu gunakan perangkat itu sebagai pemindai HP." />
      ) : (
        <>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <Label>Perangkat pemindai</Label>
              <Select value={devDipilih} onValueChange={setDevId}>
                <SelectTrigger className="w-64" data-testid="scan-device"><SelectValue placeholder="Pilih perangkat" /></SelectTrigger>
                <SelectContent>
                  {perangkat.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}{d.location ? ` — ${d.location}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground pb-2">Tap dari HP ini memakai pipeline yang sama dengan gerbang RFID: satu kartu, satu catatan hadir per hari.</p>
          </div>

          <Button size="lg" className="w-full gap-2 py-6 text-base font-bold" onClick={masukKiosk} data-testid="scan-start">
            <ScanLine className="h-5 w-5" aria-hidden /> Mulai SCAN
          </Button>
          <p className="-mt-2 text-xs text-muted-foreground">Layar penuh ala mesin absen, khusus kode QR: kamera hidup terus dan kartu anggota muncul tiap scan berhasil. Kartu NFC dipindai lewat tombol “Pindai NFC” di bawah.</p>
          <p className="-mt-3 text-[11px] text-muted-foreground" data-testid="scan-alat">{infoAlat}</p>
          {jejak.length > 0 && (
            <div className="rounded-lg bg-muted px-3 py-2 font-mono text-[10px] leading-snug" data-testid="scan-jejak" aria-live="polite">
              {jejak.map((j) => <p key={j} className="break-words">{j}</p>)}
            </div>
          )}

          <div className="rounded-xl border p-5 text-center space-y-2 min-h-40 flex flex-col justify-center" data-testid="scan-result"
            aria-live="polite" role="status">
            {!tapTerakhir ? <p className="text-muted-foreground">Tempelkan kartu ke bagian belakang HP, atau arahkan kamera ke QR.</p>
              : tapTerakhir.kartu ? (
                <div className="flex flex-col items-center gap-3">
                  <KartuAnggota anggota={tapTerakhir.kartu} sekolah={me?.active_school?.school_name}
                    status={STATUS_KARTU[tapTerakhir.status] || "ditolak"} perangkat={namaPerangkat}
                    alasan={tapTerakhir.alasan} className="w-full max-w-[19rem]" />
                  {tapTerakhir.anomali && <p className="text-xs text-amber-700">Jam perangkat menyimpang dari jam server — periksa tanggal/waktu HP.</p>}
                </div>
              ) : (
                <>
                  <p className="text-3xl font-black leading-tight">{tapTerakhir.nama || tapTerakhir.uid}</p>
                  <div className="flex justify-center"><StatusBadge map={STATUS_TAP} value={tapTerakhir.status} /></div>
                  {tapTerakhir.alasan && <p className="text-sm text-muted-foreground">{tapTerakhir.alasan}</p>}
                  {tapTerakhir.nama && <p className="font-mono text-xs text-muted-foreground">{tapTerakhir.uid}</p>}
                  {tapTerakhir.anomali && <p className="text-xs text-amber-700">Jam perangkat menyimpang dari jam server — periksa tanggal/waktu HP.</p>}
                </>
              )}
          </div>

          {galatKamera && <Notice tone="danger" testId="scan-camera-error">{galatKamera}</Notice>}
          {pesan && <Notice tone="warn" testId="scan-notice">{pesan}</Notice>}

          <div className="flex flex-wrap gap-2">
            <Button variant={nfcAktif ? "default" : "outline"} onClick={() => (nfcAktif ? matikanNFC() : nyalakanNFC())} data-testid="scan-nfc">
              <Smartphone className="h-4 w-4 mr-1" /> {nfcAktif ? "Hentikan NFC" : "Pindai NFC"}
            </Button>
            <Button variant={kameraAktif ? "default" : "outline"} onClick={() => (kameraAktif ? hentikanKamera() : bukaKamera())} data-testid="scan-camera">
              {kameraAktif ? <CameraOff className="h-4 w-4 mr-1" /> : <Camera className="h-4 w-4 mr-1" />}
              {kameraAktif ? "Tutup kamera" : "Kamera QR"}
            </Button>
            <Button variant="outline" onClick={() => kirim()} disabled={sibuk || !antrean.length} data-testid="scan-send">
              <Upload className="h-4 w-4 mr-1" /> {sibuk ? "Mengirim…" : "Kirim sekarang"}
            </Button>
          </div>

          {kameraAktif && !kiosk && (
            <video ref={pasangVideo} className="w-full max-w-sm rounded-xl border bg-black aspect-video" playsInline muted data-testid="scan-video" />
          )}

          <form className="flex flex-wrap gap-2 items-end" onSubmit={(e) => {
            e.preventDefault();
            if (catat(uidManual, "Manual")) setUidManual("");
          }}>
            <div className="space-y-1 flex-1 min-w-48">
              <Label htmlFor="scan-manual-uid">Nomor kartu (jika NFC/QR gagal)</Label>
              <Input id="scan-manual-uid" value={uidManual} onChange={(e) => setUidManual(e.target.value)}
                placeholder="mis. SDBM-2026-0003" className="font-mono" data-testid="scan-manual-input" />
            </div>
            <Button type="submit" variant="outline" disabled={uidManual.trim().length < 3} data-testid="scan-manual-add">Catat tap</Button>
          </form>

          <div className="flex flex-wrap gap-2 items-center text-sm" data-testid="scan-queue">
            <span className={antrean.length ? "font-semibold text-amber-700" : "text-muted-foreground"}>
              {antrean.length ? `${antrean.length} tap menunggu terkirim` : "Semua tap sudah terkirim"}
            </span>
            {!!antrean.length && (
              <Button variant="ghost" size="sm" onClick={() => setAntrean([])} data-testid="scan-clear">
                <Trash2 className="h-4 w-4 mr-1" /> Kosongkan antrean
              </Button>
            )}
          </div>

          {!!riwayat.length && (
            <ul className="rounded-xl border divide-y" data-testid="scan-history">
              {riwayat.map((r, i) => (
                <li key={r.id || `${r.uid}-${i}`} className="p-3 flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0">
                    <span className="font-medium">{r.nama || r.uid}</span>
                    {r.nama && <span className="block font-mono text-xs text-muted-foreground">{r.uid}</span>}
                  </span>
                  <StatusBadge map={STATUS_TAP} value={r.status} />
                </li>
              ))}
            </ul>
          )}

          <Notice tone="info" testId="scan-help">
            Satu kali saja: buka kartu di aplikasi NFC Tools → Write → <b>Add record → Text</b>, isi dengan <b>nomor
            tercetak</b> kartu (contoh <span className="font-mono">SDBM-2026-0003</span>; lihat kolom
            “Nomor tercetak” di tab Kartu RFID atau baris di samping QR pada kartunya). Jangan pilih “Format as NDEF”
            pada kartu yang sudah berisi — itu menghapus teksnya. Kartu tanpa teks NDEF hanya menyisakan nomor chipnya,
            dan nomor chip tidak berubah walau kartunya ditulis ulang.
          </Notice>

          {kiosk && createPortal(
            <ModeKiosk sekolah={me?.active_school?.school_name} perangkat={namaPerangkat}
              kameraAktif={kameraAktif} senterBisa={senterBisa} senter={senter} versi={VERSI_BUILD}
              galatKamera={galatKamera} videoRef={pasangVideo} onSenter={gantiSenter}
              onKamera={bukaKamera} onKeluar={keluarKiosk} antrean={antrean.length} tercatat={tercatat} kartu={kartuPopup} />,
            document.body,
          )}
        </>
      )}
    </div>
  );
};

// ---------- Kehadiran saya / anak saya ----------

const SayaTab = () => {
  const { me } = useAuth();
  const [hari, setHari] = useState("30");
  const q = useQuery({ queryKey: ["attendance-me", hari], queryFn: () => fetcher(`/school/attendance/me?hari=${hari}`) });
  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const orang = q.data?.people || [];
  return (
    <div className="space-y-5" data-testid="attendance-me">
      {me?.active_school?.role === "orang_tua" && <WaLangganan />}
      <div className="space-y-1">
        <Label>Periode</Label>
        <Select value={hari} onValueChange={setHari}>
          <SelectTrigger className="w-44" data-testid="me-period"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[["7", "7 hari terakhir"], ["30", "30 hari terakhir"], ["90", "90 hari terakhir"]].map(([v, l]) => (
              <SelectItem key={v} value={v}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {!orang.length && <EmptyState title="Data belum tertaut" description="Akun Anda belum terhubung ke anggota sekolah, atau relasi orang tua/wali belum diverifikasi administrator." />}
      {orang.map((o) => (
        <section key={o.membership_id} className="space-y-3" data-testid={`me-${o.membership_id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-bold">{o.full_name}</h2>
            <span className="rounded-full border px-2 py-0.5 text-xs font-semibold text-muted-foreground">{o.label}</span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="me-summary">
            {Object.keys(ATTENDANCE_STATUS).map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold">
                {ATTENDANCE_STATUS[s].label}<span className="text-muted-foreground">{o.counts[s] || 0}</span>
              </span>
            ))}
          </div>
          {!o.records.length ? <p className="text-sm text-muted-foreground">Belum ada catatan kehadiran pada periode ini.</p> : (
            <div className="rounded-xl border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tanggal</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Koreksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {o.records.map((r) => (
                    <TableRow key={r.id} data-testid={`me-record-${r.id}`}>
                      <TableCell className="font-medium">{r.attendance_date}</TableCell>
                      <TableCell><StatusBadge map={ATTENDANCE_STATUS} value={r.status} /></TableCell>
                      <TableCell><StatusBadge map={CORRECTION_STATE} value={r.correction_state} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      ))}
    </div>
  );
};

// ---------- Event & Koreksi ----------

const EventTab = () => {
  const [d, setD] = useState(hariIni());
  const [hasil, setHasil] = useState("semua");
  const [cari, setCari] = useState("");
  const params = new URLSearchParams({ d });
  if (hasil !== "semua") params.set("result", hasil);
  if (cari.trim()) params.set("q", cari.trim());
  const q = useQuery({ queryKey: ["attendance-events", d, hasil, cari], queryFn: () => fetcher(`/school/attendance/events?${params}`) });
  const qCorr = useQuery({ queryKey: ["attendance-corrections"], queryFn: () => fetcher("/school/attendance/corrections") });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="space-y-1">
          <Label htmlFor="event-tanggal">Tanggal</Label>
          <Input id="event-tanggal" type="date" value={d} onChange={(e) => setD(e.target.value)} className="w-44" data-testid="event-date" />
        </div>
        <div className="space-y-1">
          <Label>Hasil</Label>
          <Select value={hasil} onValueChange={setHasil}>
            <SelectTrigger className="w-44" data-testid="event-result-filter"><SelectValue placeholder="Semua hasil" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="semua">Semua hasil</SelectItem>
              <SelectItem value="accepted">Diterima</SelectItem>
              <SelectItem value="rejected">Ditolak</SelectItem>
              <SelectItem value="unmatched">Tak dikenal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden />
          <Input className="pl-8" placeholder="Cari UID…" value={cari} onChange={(e) => setCari(e.target.value)} data-testid="event-search" />
        </div>
      </div>
      {q.isLoading ? <LoadingState /> : q.error ? <ErrorState error={q.error} onRetry={q.refetch} />
        : !q.data?.events.length ? <EmptyState title="Tidak ada event" description="Tidak ada tap RFID pada filter ini." />
        : (
          <>
            <p className="text-xs text-muted-foreground" data-testid="event-total">Menampilkan {q.data.events.length} dari {q.data.total} event</p>
            <div className="rounded-xl border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Waktu perangkat</TableHead>
                    <TableHead>Diterima server</TableHead>
                    <TableHead>UID</TableHead>
                    <TableHead>Anggota</TableHead>
                    <TableHead>Perangkat</TableHead>
                    <TableHead>Hasil</TableHead>
                    <TableHead>Anomali jam</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {q.data.events.map((e) => (
                    <TableRow key={e.id} data-testid={`event-${e.id}`}>
                      <TableCell>{formatDate(e.event_time)}</TableCell>
                      <TableCell>{formatDate(e.received_at)}</TableCell>
                      <TableCell className="font-mono text-sm">{e.uid}</TableCell>
                      <TableCell>{e.member?.full_name || "—"}</TableCell>
                      <TableCell>{e.device_name}</TableCell>
                      <TableCell><StatusBadge map={EVENT_RESULT} value={e.result} /></TableCell>
                      <TableCell>{e.time_anomaly ? <StatusBadge map={{ ya: { label: "Ya", tone: "warn" } }} value="ya" /> : "Tidak"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      <section className="space-y-3">
        <h2 className="text-base font-bold">Riwayat koreksi</h2>
        {qCorr.isLoading ? <LoadingState rows={2} /> : !qCorr.data?.length
          ? <EmptyState title="Belum ada koreksi" description="Perubahan status kehadiran akan tercatat di sini beserta alasannya." />
          : (
            <ul className="rounded-xl border divide-y" data-testid="attendance-corrections">
              {qCorr.data.map((c) => (
                <li key={c.id} className="p-4 text-sm space-y-1">
                  <p className="font-medium">{c.member?.full_name || c.record_id} · {c.attendance_date || "—"}</p>
                  <p className="text-muted-foreground">
                    {ATTENDANCE_STATUS[c.before.status]?.label || c.before.status} → {ATTENDANCE_STATUS[c.after.status]?.label || c.after.status}
                    {" · "}oleh {c.corrected_by} · {formatDate(c.created_at)}
                  </p>
                  <p className="text-muted-foreground">Alasan: {c.reason}</p>
                </li>
              ))}
            </ul>
          )}
      </section>
    </div>
  );
};

// ---------- Dialog koreksi ----------

const KoreksiDialog = ({ record, onClose }) => {
  const qc = useQueryClient();
  const [status, setStatus] = useState("IZIN");
  const [reason, setReason] = useState("");
  const simpan = async () => {
    try {
      await api.patch(`/school/attendance/records/${record.id}`, { status, reason });
      toast.success("Koreksi kehadiran tersimpan.");
      qc.invalidateQueries();
      onClose();
    } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <Dialog open={!!record} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Koreksi kehadiran</DialogTitle>
          <DialogDescription>
            {record?.member?.full_name} · {record?.attendance_date}. Koreksi tercatat di audit dan tidak menghapus event asli.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Status kehadiran</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="correction-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(ATTENDANCE_STATUS).map((s) => <SelectItem key={s} value={s}>{ATTENDANCE_STATUS[s].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="correction-reason">Alasan (wajib)</Label>
            <Textarea id="correction-reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="correction-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={simpan} disabled={reason.trim().length < 5} data-testid="correction-save">Simpan koreksi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Notifikasi WhatsApp ----------

const WA_STATUS = {
  antre: { label: "Antre", tone: "info" },
  terkirim: { label: "Terkirim", tone: "ok" },
  simulasi: { label: "Simulasi", tone: "info" },
  gagal: { label: "Gagal", tone: "danger" },
  dibatalkan: { label: "Dibatalkan", tone: "warn" },
};

// Kabar balik dari server Meta lewat webhook — bukan status pengiriman SIST.
const WA_DELIVER = { sent: "diterima Meta", delivered: "sampai di HP", read: "dibaca", failed: "gagal di Meta" };

const WaLangganan = () => {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["wa-subscriptions"], queryFn: () => fetcher("/school/wa/subscriptions") });
  const [form, setForm] = useState({});
  if (q.isLoading) return <LoadingState rows={2} />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const daftar = q.data.subscriptions || [];
  if (!daftar.length) return null;
  const nilai = (s, k) => form[s.student_membership_id]?.[k] ?? s[k];
  const ubah = (s, k, v) => setForm((f) => ({
    ...f, [s.student_membership_id]: { phone: nilai(s, "phone"), enabled: nilai(s, "enabled"), [k]: v },
  }));
  const simpan = async (s) => {
    try {
      await api.put(`/school/wa/subscriptions/${s.student_membership_id}`,
        { phone: nilai(s, "phone"), enabled: nilai(s, "enabled") });
      toast.success("Pengaturan notifikasi WhatsApp tersimpan.");
      qc.invalidateQueries({ queryKey: ["wa-subscriptions"] });
    } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <section className="rounded-xl border p-4 space-y-3" data-testid="wa-subscriptions">
      <h2 className="text-base font-bold">Notifikasi WhatsApp kehadiran</h2>
      <p className="text-sm text-muted-foreground">
        Bila aktif, Anda menerima pesan WhatsApp setiap anak tercatat hadir lewat tap RFID atau pemindai HP.
        Dengan mengaktifkan, Anda setuju menerima nama anak, status kehadiran, dan waktu tap di WhatsApp Anda.
      </p>
      {daftar.map((s) => (
        <div key={s.student_membership_id} className="rounded-lg border p-3 space-y-2" data-testid={`wa-sub-${s.student_membership_id}`}>
          <p className="text-sm font-semibold">
            {s.student_name} <span className="font-normal text-muted-foreground">· wali {s.relation || "terverifikasi"}</span>
          </p>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1 flex-1 min-w-48">
              <Label htmlFor={`wa-phone-${s.student_membership_id}`}>Nomor WhatsApp Anda</Label>
              <Input id={`wa-phone-${s.student_membership_id}`} value={nilai(s, "phone")} placeholder="mis. 081234567890"
                onChange={(e) => ubah(s, "phone", e.target.value)} data-testid={`wa-phone-${s.student_membership_id}`} />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Switch id={`wa-on-${s.student_membership_id}`} checked={!!nilai(s, "enabled")}
                onCheckedChange={(v) => ubah(s, "enabled", v)} data-testid={`wa-enabled-${s.student_membership_id}`} />
              <Label htmlFor={`wa-on-${s.student_membership_id}`}>Aktif</Label>
            </div>
            <Button onClick={() => simpan(s)} data-testid={`wa-sub-save-${s.student_membership_id}`}>Simpan</Button>
          </div>
          {s.consent_at && <p className="text-xs text-muted-foreground">Persetujuan diberikan {formatDate(s.consent_at)}.</p>}
        </div>
      ))}
    </section>
  );
};

const WaTab = () => {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["wa-settings"], queryFn: () => fetcher("/school/wa/settings") });
  const qOut = useQuery({ queryKey: ["wa-outbox"], queryFn: () => fetcher("/school/wa/outbox?limit=50") });
  const [form, setForm] = useState(null);
  const [uji, setUji] = useState("");
  useEffect(() => { if (q.data && !form) setForm({ ...q.data, meta_token: "" }); }, [q.data, form]);
  if (q.isLoading || !form) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const simpan = async () => {
    try {
      await api.put("/school/wa/settings", form);
      toast.success("Pengaturan WhatsApp tersimpan.");
      qc.invalidateQueries({ queryKey: ["wa-settings"] });
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const kirimUji = async () => {
    try {
      const r = await api.post("/school/wa/test", { phone: uji }).then((x) => x.data);
      if (r.status === "gagal") toast.error(`Uji kirim gagal: ${r.error}`);
      else toast.success(`Uji kirim berstatus ${WA_STATUS[r.status]?.label || r.status}.`);
      qc.invalidateQueries({ queryKey: ["wa-outbox"] });
    } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <div className="space-y-5" data-testid="wa-settings">
      <div className="rounded-xl border p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-semibold">Notifikasi WhatsApp ke orang tua</p>
            <p className="text-sm text-muted-foreground">
              Dikirim saat siswa pertama kali tercatat hadir pada hari itu lewat tap RFID atau pemindai HP.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="wa-enabled" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="wa-enabled" />
            <Label htmlFor="wa-enabled">Aktif</Label>
          </div>
        </div>
        <div className="space-y-1">
          <Label>Penyedia</Label>
          <Select value={form.provider} onValueChange={(v) => setForm({ ...form, provider: v })}>
            <SelectTrigger className="w-64" data-testid="wa-provider"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="simulasi">Simulasi (pesan hanya dicatat)</SelectItem>
              <SelectItem value="meta">Meta WhatsApp Cloud API</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.provider === "meta" && (
          <div className="grid gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="wa-token">Token akses Meta</Label>
              <Input id="wa-token" type="password" value={form.meta_token} placeholder={q.data.meta_token || "tempel token permanen"}
                onChange={(e) => setForm({ ...form, meta_token: e.target.value })} data-testid="wa-token" />
              <p className="text-xs text-muted-foreground">Buat token sistem permanen di Business Suite, bukan token sementara konsol (kedaluwarsa 24 jam).</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="wa-pnid">Phone Number ID</Label>
              <Input id="wa-pnid" value={form.phone_number_id} onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
                inputMode="numeric" placeholder="mis. 123456789012345" data-testid="wa-pnid" />
              <p className="text-xs text-muted-foreground">Angka ID dari WhatsApp &gt; API Setup. Bukan nomor telepon yang berawalan +.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="wa-template">Nama template disetujui</Label>
              <Input id="wa-template" value={form.template_name} onChange={(e) => setForm({ ...form, template_name: e.target.value })}
                placeholder="mis. sist_presensi" data-testid="wa-template" />
              <p className="text-xs text-muted-foreground">Nama yang Anda ketik sendiri saat membuat template di WhatsApp Manager &gt; Message templates, huruf kecil tanpa spasi. Kosongkan untuk mengirim teks biasa — hanya berlaku pada nomor uji Meta.</p>
            </div>
          </div>
        )}
        <Notice tone="info" testId="wa-template-hint">
          Buat template utility di Meta Business Manager dengan isi persis: “{q.data.template_hint}”
          Urutan variabel: nama wali, nama siswa, kelas, status, tanggal, jam, nama sekolah.
          Selama masih memakai nomor uji Meta, setiap nomor tujuan harus didaftarkan lebih dulu di
          “Kelola daftar” penerima uji, dan template harus berstatus disetujui.
        </Notice>
        <div className="flex justify-end">
          <Button onClick={simpan} data-testid="wa-save">Simpan pengaturan</Button>
        </div>
      </div>
      <div className="rounded-xl border p-4 space-y-3" data-testid="wa-webhook">
        <div>
          <p className="font-semibold">Webhook status pesan</p>
          <p className="text-sm text-muted-foreground">
            Tempel dua nilai ini di Meta for Developers &gt; app Anda &gt; WhatsApp &gt; Configuration &gt; Webhook.
            Tanpa webhook pun pesan tetap terkirim; webhook hanya membuat riwayat tahu pesan sudah sampai di HP.
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label>Callback URL</Label>
            <div className="flex gap-2">
              <Input readOnly value={`${window.location.origin}${q.data.webhook_path}`} className="font-mono text-xs"
                onFocus={(e) => e.target.select()} data-testid="wa-callback-url" />
              <Salin teks={`${window.location.origin}${q.data.webhook_path}`} />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Verify token</Label>
            <div className="flex gap-2">
              <Input readOnly value={q.data.webhook_token || "(tekan Simpan pengaturan untuk membuat token)"}
                className="font-mono text-xs" onFocus={(e) => e.target.select()} data-testid="wa-verify-token" />
              {q.data.webhook_token ? <Salin teks={q.data.webhook_token} />
                : <Button variant="outline" size="sm" onClick={simpan}>Buat token</Button>}
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Setelah menekan Verify and save di Meta, kirim uji satu pesan — status di riwayat akan berubah menjadi
          “sampai di HP” begitu WhatsApp melapor.
        </p>
      </div>
      <div className="rounded-xl border p-4 space-y-2">
        <p className="font-semibold">Uji kirim</p>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="space-y-1 flex-1 min-w-48">
            <Label htmlFor="wa-test-phone">Nomor tujuan uji</Label>
            <Input id="wa-test-phone" value={uji} onChange={(e) => setUji(e.target.value)} placeholder="mis. 081234567890" data-testid="wa-test-phone" />
          </div>
          <Button variant="outline" onClick={kirimUji} disabled={uji.trim().length < 9} data-testid="wa-test-send">Kirim uji</Button>
        </div>
        <p className="text-xs text-muted-foreground">Uji kirim memakai template yang sama; pada provider simulasi pesan hanya dicatat di bawah.</p>
      </div>
      <section className="space-y-2">
        <h2 className="text-base font-bold">Riwayat pengiriman</h2>
        {qOut.isLoading ? <LoadingState rows={2} /> : qOut.error ? <ErrorState error={qOut.error} onRetry={qOut.refetch} />
          : !qOut.data.outbox.length ? <EmptyState title="Belum ada pengiriman" description="Pesan muncul di sini setelah siswa presensi atau setelah uji kirim." />
          : (
            <div className="rounded-xl border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Waktu</TableHead>
                    <TableHead>Siswa</TableHead>
                    <TableHead>Wali</TableHead>
                    <TableHead>Nomor</TableHead>
                    <TableHead>Sumber</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Keterangan</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {qOut.data.outbox.map((o) => (
                    <TableRow key={o.id} data-testid={`wa-out-${o.id}`}>
                      <TableCell>{formatDate(o.created_at)}</TableCell>
                      <TableCell>{o.student_name || "—"}</TableCell>
                      <TableCell>{o.guardian_name || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{o.phone_masked}</TableCell>
                      <TableCell>{o.source}</TableCell>
                      <TableCell><StatusBadge map={WA_STATUS} value={o.status} /></TableCell>
                      <TableCell className="text-xs text-muted-foreground" data-testid={`wa-delivery-${o.id}`}>
                        {o.error || (o.wa_status
                          ? `Meta: ${WA_DELIVER[o.wa_status] || o.wa_status}${o.wa_error ? ` — ${o.wa_error}` : ""}`
                          : "—")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
      </section>
    </div>
  );
};

// ---------- Halaman ----------

export default function AttendancePage() {
  const { me } = useAuth();
  const dapatKelola = useSchoolCan("attendance:manage");
  const dapatAbsen = useSchoolCan("attendance:take");
  const dapatPindai = useSchoolCan("attendance:scan");
  const dapatBaca = useSchoolCan("attendance:read");
  const [koreksi, setKoreksi] = useState(null);
  const qc = useQueryClient();
  const tabs = [
    ...(dapatAbsen ? [["manual", "Presensi Manual", <PresensiManualTab key="manual" />]] : []),
    ...(dapatPindai ? [["pindai", "Pemindai HP", <ScannerTab key="pindai" />]] : []),
    ...(dapatBaca ? [
      ["monitor", "Monitor Hari Ini", <MonitorTab key="monitor" dapatKelola={dapatKelola} onKoreksi={setKoreksi} />],
      ["rekap", "Rekap Harian", <RekapTab key="rekap" dapatKelola={dapatKelola} onKoreksi={setKoreksi} />],
      ["kartu", "Kartu RFID", <KartuTab key="kartu" dapatKelola={dapatKelola} />],
      // Cetak kartu memuat foto + nomor induk seluruh anggota, jadi hanya pengurus yang mengelolanya.
      ...(dapatKelola ? [["kartu-id", "Cetak Kartu ID", <CetakKartuTab key="kartu-id" />]] : []),
      ["perangkat", "Perangkat", <PerangkatTab key="perangkat" dapatKelola={dapatKelola} />],
      ["event", "Event & Koreksi", <EventTab key="event" />],
    ] : []),
    ...(dapatKelola ? [["wa", "Notifikasi WA", <WaTab key="wa" />]] : []),
    ["saya", me?.active_school?.role === "orang_tua" ? "Kehadiran Anak" : "Kehadiran Saya", <SayaTab key="saya" />],
  ];
  const tabAktif = dapatBaca ? "monitor" : tabs[0][0];

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <PageHeader
        eyebrow="Presensi"
        title="Presensi"
        description="Catat kehadiran lewat gerbang RFID, pemindai HP (NFC/QR), atau presensi manual guru. Siswa dan orang tua melihat rekap kehadirannya sendiri."
        actions={<Button variant="outline" onClick={() => qc.invalidateQueries()} data-testid="attendance-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Segarkan</Button>}
      />
      {!tabs.length ? <EmptyState title="Akses ditolak" description="Peran Anda tidak memiliki izin pada halaman presensi." /> : (
      <Tabs defaultValue={tabAktif}>
        <TabsList className="flex-wrap h-auto">
          {tabs.map(([k, label]) => <TabsTrigger key={k} value={k} data-testid={`attendance-tab-${k}`}>{label}</TabsTrigger>)}
        </TabsList>
        {tabs.map(([k, , el]) => <TabsContent key={k} value={k} className="mt-6">{el}</TabsContent>)}
      </Tabs>
      )}
      <KoreksiDialog record={koreksi} onClose={() => setKoreksi(null)} />
    </div>
  );
}
